import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DataContext } from '../../../context/DataContext';
import { theme } from '../../../styles/theme';
import { Button } from '../../common/Button';

const LevelSelectionContainer = styled.div`
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  margin-right: ${theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: 24px;
  color: ${theme.colors.deepNavy};
  text-align: center;
  flex-grow: 1;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  font-weight: bold;
  margin-bottom: ${theme.spacing.xs};
  text-align: center;
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.deepNavy};
  margin-bottom: ${theme.spacing.lg};
`;

const LevelCard = styled.div`
  background-color: ${({ selected }) => (selected ? theme.colors.iceBlue : theme.colors.white)};
  border: 1px solid ${({ selected }) => (selected ? theme.colors.brandYellow : theme.colors.softGray)};
  border-radius: ${theme.borderRadius.large};
  padding: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  cursor: pointer;
  box-shadow: ${theme.shadows.subtle};
`;

const LevelIcon = styled.img`
  width: 40px;
  height: 40px;
  margin-right: ${theme.spacing.md};
`;

const LevelContent = styled.div`
  flex-grow: 1;
`;

const LevelTitle = styled.h2`
  font-size: ${theme.typography.fontSizes.large};
  color: ${theme.colors.deepNavy};
  font-weight: bold;
  margin: 0;
`;

const LevelDescription = styled.p`
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
  margin: ${theme.spacing.xs} 0;
`;

const LevelExamples = styled.p`
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid ${theme.colors.softGray};
  background-color: ${({ selected }) => (selected ? theme.colors.brandYellow : theme.colors.white)};
`;

const FooterText = styled.p`
  font-size: ${theme.typography.fontSizes.small};
  color: ${theme.colors.slateGray};
  text-align: center;
  margin-top: auto;
  padding-top: ${theme.spacing.md};
`;

const ConfirmButton = styled(Button)`
  margin-top: ${theme.spacing.md};
`;

const levels = [
  {
    id: 1,
    icon: '/icons/icon_streetlight.png',
    title: 'Level 1 - Streetlight Advocate',
    description: "I'm just getting started or prefer small, personal acts of advocacy.",
    examples: 'Examples: sharing resources, learning, donating',
  },
  {
    id: 2,
    icon: '/icons/icon_daylight.png',
    title: 'Level 2 - Daylight Advocate',
    description: 'I\'m ready for more visible, collaborative work in my community.',
    examples: 'Examples: organizing events, community action',
  },
  {
    id: 3,
    icon: '/icons/icon_sunshield.png',
    title: 'Level 3 - Sunshield Advocate',
    description: "I'm working on long-term, strategic systems change.",
    examples: 'Examples: policy work, coalition-building',
  },
];

export const LevelSelectionScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addCause } = useContext(DataContext);

  const [causes] = useState(location.state?.causes || []);
  const [currentCauseIndex, setCurrentCauseIndex] = useState(0);
  const [selectedLevels, setSelectedLevels] = useState({});

  useEffect(() => {
    if (!location.state?.causes || location.state.causes.length === 0) {
      navigate('/causes');
    }
  }, [location.state, navigate]);

  const handleLevelSelect = (levelId) => {
    setSelectedLevels(prev => ({ ...prev, [causes[currentCauseIndex].id]: levelId }));
  };

  const handleNext = () => {
    if (currentCauseIndex < causes.length - 1) {
      setCurrentCauseIndex(currentCauseIndex + 1);
    } else {
      handleConfirm();
    }
  };

  const handleConfirm = async () => {
    try {
      for (const cause of causes) {
        const levelId = selectedLevels[cause.id] || 1; // Default to level 1
        const levelData = levels.find(l => l.id === levelId);
        const causeWithLevel = { ...cause, level: levelData };
        await addCause(causeWithLevel);
      }
      navigate('/');
    } catch (error) {
      console.error("Failed to add causes with levels:", error);
      alert("There was an issue saving your causes. Please try again.");
    }
  };
  
  const currentCause = causes[currentCauseIndex];

  if (!currentCause) {
    return <div>Loading...</div>;
  }

  return (
    <LevelSelectionContainer>
      <Header>
        <BackButton onClick={() => navigate(-1)}>&#x2190;</BackButton>
        <Title>Choose Your Level</Title>
      </Header>
      <Subtitle>
        <LevelIcon src={currentCause.icon} alt="" />
        For: {currentCause.title}
      </Subtitle>
      <Description>Select the level that best describes your current advocacy:</Description>

      {levels.map((level) => (
        <LevelCard
          key={level.id}
          selected={selectedLevels[currentCause.id] === level.id}
          onClick={() => handleLevelSelect(level.id)}
        >
          <LevelIcon src={level.icon} alt="" />
          <LevelContent>
            <LevelTitle>{level.title}</LevelTitle>
            <LevelDescription>{level.description}</LevelDescription>
            <LevelExamples>{level.examples}</LevelExamples>
          </LevelContent>
          <RadioButton selected={selectedLevels[currentCause.id] === level.id} />
        </LevelCard>
      ))}

      <FooterText>
        You can stay at any level indefinitely. Change later if needed.
      </FooterText>
      <ConfirmButton primary onClick={handleNext}>
        {currentCauseIndex < causes.length - 1 ? 'Next Cause' : 'Confirm'}
      </ConfirmButton>
    </LevelSelectionContainer>
  );
};
